import { ModelRow, SortKey, SortDir, regionCity } from '../types';
import { isRegionActive, lifecycleStatus, rowKey, tierLabel } from '../lib/models';
import { CopyButton } from './CopyButton';
import { CloudPill } from './CloudPill';

function criAccessLabel(regions: ModelRow['regions']): { label: string; cls: string } {
  const criCount = regions.filter(r => r.isCri).length;
  if (criCount === 0) return { label: 'Native', cls: 'badge-no' };
  if (criCount === regions.length) return { label: 'CRI', cls: 'badge-cri' };
  return { label: 'Mixed', cls: 'badge-mixed' };
}

function statusLabel(regions: ModelRow['regions']): { label: string; cls: string } {
  const status = lifecycleStatus(regions);
  if (status === 'ACTIVE') return { label: 'Active', cls: 'badge-active' };
  if (status === 'LEGACY') return { label: 'Legacy', cls: 'badge-legacy' };
  return { label: 'Mixed', cls: 'badge-mixed' };
}

function RegionBadge({ region, cri, legacy, tiers, regionLabels }: {
  region: string;
  cri?: boolean;
  legacy?: boolean;
  tiers?: string[];
  regionLabels: Record<string, string>;
}) {
  const tier = tierLabel(tiers ?? []);
  return (
    <span
      className={`badge ${cri ? 'badge-region-cri' : 'badge-region'}${legacy ? ' badge-region-legacy' : ''}`}
      title={`${cri ? 'via cross-region routing' : 'in-region'}${tier ? ` · ${tier}` : ''}${legacy ? ' · legacy/deprecated in this region' : ''}`}
    >
      {region}
      <span className="badge-city">{regionCity(region, regionLabels)}{tier ? ` · ${tier}` : ''}</span>
    </span>
  );
}


interface ColDef { key: SortKey; label: string; tooltip?: string }
const COLS: ColDef[] = [
  { key: 'providerName', label: 'Provider' },
  { key: 'modelName',    label: 'Model' },
  { key: 'modelId',      label: 'Model ID' },
  { key: 'regions',      label: 'Regions' },
  {
    key: 'isCri',
    label: 'Access',
    tooltip: 'Native: served in-region where listed. CRI: reachable only via cross-region routing, which can process data in another region within the same geography — open a model for its exact per-source routing.',
  },
];

interface Props {
  rows: ModelRow[];
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  onOpenModel: (modelId: string) => void;
  regionLabels: Record<string, string>;
}

export function ModelTable({ rows, sortKey, sortDir, onSort, onOpenModel, regionLabels }: Props) {
  const colCount = COLS.length + 4; // +4 for Cloud, Input, Output, Status

  function sortIcon(key: SortKey) {
    if (key !== sortKey) return <span className="sort-icon">↕</span>;
    return <span className="sort-icon">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  const legend = (
    <div className="table-legend">
      <span className="badge badge-region" style={{ pointerEvents: 'none' }} aria-hidden="true">
        eu-west-2<span className="badge-city">London</span>
      </span> In-region
      <span className="badge badge-region-cri" style={{ pointerEvents: 'none' }} aria-hidden="true">
        eu-west-1<span className="badge-city">Ireland</span>
      </span> Via routing only
      <span className="legend-sep">·</span>
      Select a model for exact per-source routing
    </div>
  );

  return (
    <div>
      {legend}

      {/* ── Desktop table ── */}
      <div className="table-wrap">
        <table>
          <caption className="sr-only">Model availability by cloud, provider, region, and access type</caption>
          <thead>
            <tr>
              <th scope="col">Cloud</th>
              {COLS.map(col => (
                <th
                  key={col.key}
                  scope="col"
                  className={col.key === sortKey ? 'sorted' : ''}
                  aria-sort={col.key === sortKey ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  <button type="button" className="th-btn" onClick={() => onSort(col.key)}>
                    {col.label}
                    {sortIcon(col.key)}
                  </button>
                  {col.tooltip && (
                    <span className="tooltip-wrap" tabIndex={0} aria-label={col.tooltip}>
                      {' '}ⓘ
                      <span className="tooltip-text" role="tooltip">{col.tooltip}</span>
                    </span>
                  )}
                </th>
              ))}
              <th scope="col">Input</th>
              <th scope="col">Output</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr className="empty-row">
                <td colSpan={colCount}>No models match the current filters.</td>
              </tr>
            ) : rows.map(row => {
              const access = criAccessLabel(row.regions);
              return (
                <tr key={rowKey(row)} className="row-clickable" onClick={() => onOpenModel(rowKey(row))}>
                  <td><CloudPill cloud={row.cloud} /></td>
                  <td>{row.providerName}</td>
                  <td>
                    <button
                      type="button"
                      className="row-model-name row-open-btn"
                      aria-label={`View details for ${row.modelName}`}
                      onClick={e => { e.stopPropagation(); onOpenModel(rowKey(row)); }}
                    >
                      {row.modelName}
                    </button>
                  </td>
                  <td>
                    <span className="model-id" title={row.modelId}>{row.modelId}</span>
                    <CopyButton text={row.modelId} />
                  </td>
                  <td>
                    <div className="regions-cell">
                      {row.regions.map(re => (
                        <RegionBadge key={re.region} region={re.region} cri={re.isCri} legacy={!isRegionActive(re)} tiers={re.inferenceTypesSupported} regionLabels={regionLabels} />
                      ))}
                    </div>
                  </td>
                  <td><span className={`badge ${access.cls}`}>{access.label}</span></td>
                  <td>
                    <div className="modalities-cell">
                      {row.inputModalities.map(m => <span key={m} className="badge badge-modality">{m.toLowerCase()}</span>)}
                    </div>
                  </td>
                  <td>
                    <div className="modalities-cell">
                      {row.outputModalities.map(m => <span key={m} className="badge badge-modality">{m.toLowerCase()}</span>)}
                      {row.streamingSupported && (
                        <span className="badge badge-stream" title="Supports streaming responses">stream</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {(() => {
                      const status = statusLabel(row.regions);
                      return <span className={`badge ${status.cls}`}>{status.label}</span>;
                    })()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Mobile cards ── */}
      <div className="model-cards">
        {rows.length === 0 ? (
          <p className="empty-message">No models match the current filters.</p>
        ) : rows.map(row => {
          const access = criAccessLabel(row.regions);
          const status = statusLabel(row.regions);
          return (
            <div
              key={rowKey(row)}
              className="model-card"
              role="button"
              tabIndex={0}
              aria-label={`View details for ${row.modelName} (${row.providerName})`}
              onClick={() => onOpenModel(rowKey(row))}
              onKeyDown={e => {
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpenModel(rowKey(row));
                }
              }}
            >
              <div className="card-header">
                <span className="card-provider">{row.providerName}</span>
                <div className="card-badges">
                  <CloudPill cloud={row.cloud} />
                  <span className={`badge ${access.cls}`}>{access.label}</span>
                  {status.label !== 'Active' && <span className={`badge ${status.cls}`}>{status.label}</span>}
                </div>
              </div>

              <div className="card-name">{row.modelName}</div>

              <div className="card-model-id">
                <code className="model-id">{row.modelId}</code>
                <CopyButton text={row.modelId} />
              </div>

              <div className="card-section">
                <span className="card-section-label">Regions</span>
                <div className="regions-cell card-regions-cell">
                  {row.regions.map(re => (
                    <RegionBadge key={re.region} region={re.region} cri={re.isCri} legacy={!isRegionActive(re)} tiers={re.inferenceTypesSupported} regionLabels={regionLabels} />
                  ))}
                </div>
              </div>

              <div className="card-modalities">
                <span className="card-section-label">In</span>
                {row.inputModalities.map(m => <span key={m} className="badge badge-modality">{m.toLowerCase()}</span>)}
                <span className="card-section-label">Out</span>
                {row.outputModalities.map(m => <span key={m} className="badge badge-modality">{m.toLowerCase()}</span>)}
                {row.streamingSupported && (
                  <span className="badge badge-stream" title="Supports streaming responses">stream</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
